// Emission side of the session-fiducial id-book (the PRE-FLIP gap fix for the
// Zoom watermark feature). The on-screen AprilTag encodes only an opaque
// per-session tag index; WITHOUT the persisted id-book the recording is
// undecodable. This module guarantees that every tag ever painted is covered
// by a persisted book the offline decoder can retrieve:
//
//   - the session eventId (the decoder's retrieval/join key) is resolved from
//     the tutor's session link (?sessionEventId=...), kept per-tab;
//   - the book is mirrored to sessionStorage so a reload REHYDRATES the same
//     question-id -> tag-index assignments (a fresh book would silently remap
//     tags and corrupt decode of already-recorded frames);
//   - each time a question-id is first painted, the FULL cumulative book
//     ({eventId, recordingId, qToTag} — the exact shape the decode spine's
//     IdBook.from_json() loads) is PUT to the backend store; because every
//     emission carries the whole map, any dropped request is healed by the
//     next one (plus a bounded in-emitter retry).

import { SessionIdBook } from "./session-fiducial";

// Query param a tutor session link uses to carry the calendar-event-id — the
// key the decoder joins on (same eventId spine as notes-to-quiz AI_SESSION).
export const SESSION_EVENT_ID_PARAM = "sessionEventId";

// Per-tab (sessionStorage) keys. A tab hosts one tutoring session at a time.
export const EVENT_ID_STORAGE_KEY = "sessionFiducial.eventId";
export const ID_BOOK_STORAGE_KEY = "sessionFiducial.idBook";

// Fallback eventIds (no session link param) are generated with this prefix so
// the decode side can tell a real calendar-event join key from an opaque
// web-session id that needs an out-of-band (time-window) join.
export const GENERATED_EVENT_ID_PREFIX = "web-";

// One bounded retry so a transient failure on the LAST question of a session
// (no later navigation to heal it) still gets a second chance.
export const EMIT_RETRY_DELAY_MS = 5000;

const generateEventId = () => {
  const uuid =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return `${GENERATED_EVENT_ID_PREFIX}${uuid}`;
};

const safeGet = (storage, key) => {
  try {
    return storage ? storage.getItem(key) : null;
  } catch (_) {
    return null;
  }
};

const safeSet = (storage, key, value) => {
  try {
    if (storage) {
      storage.setItem(key, value);
    }
  } catch (_) {
    // Storage full/blocked: emission still works, only reload-rehydration degrades.
  }
};

// Resolve this tab's session eventId: explicit link param wins (and sticks),
// then the tab's previously resolved id, else a generated opaque fallback.
export const resolveSessionEventId = ({ search, storage } = {}) => {
  const fromParam = (new URLSearchParams(search || "").get(SESSION_EVENT_ID_PARAM) || "").trim();
  if (fromParam) {
    safeSet(storage, EVENT_ID_STORAGE_KEY, fromParam);
    return fromParam;
  }
  const stored = (safeGet(storage, EVENT_ID_STORAGE_KEY) || "").trim();
  if (stored) {
    return stored;
  }
  const generated = generateEventId();
  safeSet(storage, EVENT_ID_STORAGE_KEY, generated);
  return generated;
};

// Load this tab's id-book: rehydrate the stored one when it belongs to the
// same session (eventId match), otherwise start fresh for the new session.
export const loadSessionIdBook = ({ search, storage } = {}) => {
  const eventId = resolveSessionEventId({ search, storage });
  const raw = safeGet(storage, ID_BOOK_STORAGE_KEY);
  if (raw) {
    try {
      const plain = JSON.parse(raw);
      if (plain && plain.eventId === eventId) {
        return SessionIdBook.fromJSON(plain);
      }
    } catch (_) {
      // Corrupt mirror: fall through to a fresh book.
    }
  }
  return new SessionIdBook({ eventId });
};

// Emitter around one SessionIdBook. sync() persists the book's current state
// to sessionStorage and, when the state changed since the last successful
// emission, PUTs the full book to the backend store. postIdBook(payload) must
// resolve to the apiService result shape (an object with errStatus/errMsg on
// failure — apiService never rejects).
export const createIdBookEmitter = ({ idBook, postIdBook, storage } = {}) => {
  let lastEmitted = null; // serialized form last ACKed by the backend
  let inFlight = null;
  let retryTimer = null;
  let disposed = false;

  const clearRetry = () => {
    if (retryTimer) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
  };

  const sync = () => {
    if (disposed || !idBook || idBook.size === 0) {
      return Promise.resolve(false);
    }
    const payload = idBook.toJSON();
    const serialized = JSON.stringify(payload);
    safeSet(storage, ID_BOOK_STORAGE_KEY, serialized);

    if (serialized === lastEmitted) {
      return Promise.resolve(false);
    }
    if (inFlight) {
      // Full-book emissions are cumulative — the follow-up sync scheduled on
      // settle will carry this state too.
      return inFlight;
    }
    clearRetry();
    inFlight = Promise.resolve(postIdBook(payload)).then(
      (result) => {
        inFlight = null;
        const failed = !result || result.errStatus || result.errMsg;
        if (!failed) {
          lastEmitted = serialized;
          // The book may have grown while this request was in flight.
          if (JSON.stringify(idBook.toJSON()) !== serialized) {
            return sync();
          }
          return true;
        }
        scheduleRetry();
        return false;
      },
      () => {
        inFlight = null;
        scheduleRetry();
        return false;
      }
    );
    return inFlight;
  };

  const scheduleRetry = () => {
    if (disposed || retryTimer) {
      return;
    }
    retryTimer = setTimeout(() => {
      retryTimer = null;
      sync();
    }, EMIT_RETRY_DELAY_MS);
  };

  const dispose = () => {
    disposed = true;
    clearRetry();
  };

  return { sync, dispose };
};
